import { selectCurrentHotelName } from 'accessors'
import ErrorIcon from 'components/ReusableComponents/BackgroundImage/ErrorIcon'
import TextInput from 'components/ReusableComponents/ManageHotel/TextInput'
import Toggle from 'components/ReusableComponents/Toggle'
import MediaUploader from 'features/ManageCampaigns/MediaUploader'
import TextArea from 'features/ManageCampaigns/TextArea'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import styles from './StepContentEditor.module.scss'

const urlRegex = /^(http|https)?:\/\/\S+/i

function getBaseTags(hotelName) {
  return [
    {
      id: '__hotelName__',
      display: 'hotelName',
      code: '@[hotelName](__hotelName__)',
      length: hotelName.length
    },
    {
      id: '__guestName__',
      display: 'firstName',
      code: '@[firstName](__guestName__)',
      length: 8
    },
    {
      id: '__guestLastName__',
      display: 'lastName',
      code: '@[lastName](__guestLastName__)',
      length: 8
    },
    {
      id: '__checkoutDate__',
      display: 'checkoutDate',
      length: 11
    }
  ]
}

function getPlainTextLength(template = '') {
  return template.replace(/@\[([^\]]+)\]\([^)]+\)/g, '$1').length
}

function StepTextEditor({
  messageTemplate,
  onTextChange,
  messageMedia,
  setMessageMedia,
  usedAllTags,
  setUsedAllTags,
  mediaTag,
  requiredTagIds,
  optionalTagIds = ['__guestLastName__'],
  customUrl,
  setCustomUrl,
  isCustomUrlAllowed
}) {
  const hotelName = useSelector(selectCurrentHotelName)

  const [useCustomUrl, setUseCustomUrl] = useState(Boolean(customUrl))

  const isCustomUrlValid = customUrl ? Boolean(customUrl.match(urlRegex)) : false

  function handleUpdateUsedTags(usedTags) {
    setUsedAllTags(
      requiredTagIds.every(tag => usedTags.find(usedTag => usedTag.id === tag))
    )
  }

  function handleChangeCustomUrl(event) {
    setUseCustomUrl(event.target.checked)

    if (!event.target.checked) {
      setCustomUrl(null)
    }
  }

  return (
    <>
      <div className={styles.field}>
        <div className={styles.fieldLabelRow}>
          <span className={styles.fieldLabel}>Text message</span>
          <span className={styles.fieldHint}>
            {getPlainTextLength(messageTemplate)} chars
          </span>
        </div>
        <TextArea
          baseTags={getBaseTags(hotelName) || []}
          previewPrefix="123"
          value={messageTemplate}
          handleUpdateUsedTags={handleUpdateUsedTags}
          handleChange={onTextChange}
          isErred={!usedAllTags}
          requiredTagIds={requiredTagIds}
          optionalTagIds={optionalTagIds}
        />
      </div>
      {isCustomUrlAllowed && (
        <div className={styles.field}>
          <Toggle
            label="Use a custom invite link"
            checked={useCustomUrl}
            handleChange={handleChangeCustomUrl}
            className={styles.inlineToggle}
          />
          <p className={styles.fieldHint}>
            Appended after your message, shortened per guest.
          </p>
          {useCustomUrl && (
            <div>
              <TextInput
                className={styles.fullWidthInput}
                autoFocus
                placeholder="https://"
                value={customUrl || ''}
                onChange={event => setCustomUrl(event.target.value)}
                valid={isCustomUrlValid}
              />
              {customUrl && !isCustomUrlValid && (
                <p className={styles.fieldError}>
                  Enter a valid link starting with http or https
                </p>
              )}
            </div>
          )}
        </div>
      )}
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Media</span>
        {!messageMedia && (
          <div className={styles.recommendation}>
            <ErrorIcon />
            <p>
              Adding an image or a video is <strong>strongly recommended</strong> to get
              the best results from the drip campaign
            </p>
          </div>
        )}
        <MediaUploader
          hasTitle={false}
          selectedMedia={messageMedia}
          setSelectedMedia={setMessageMedia}
          removeSelectedMedia={() => setMessageMedia(null)}
          acceptedFileTypes={['image/*', 'video/*', 'application/pdf']}
          previewCount={4}
          mediaTag={mediaTag}
        />
      </div>
    </>
  )
}

export default StepTextEditor
